import './SelectableList.scss';
import { ReactNode } from 'react';
import clsx from 'clsx';
import { MdDeleteOutline } from 'react-icons/md';

export interface ISelectableListItem {
    /** Ключ строки — он же значение, которое уходит наверх. */
    key: string;
    /** Главный текст строки. */
    title: string;
    /** Пояснение под заголовком (документ, категория, исполнитель). */
    subtitle?: string;
    /** Значение справа: размер файла, дата, статус. */
    meta?: ReactNode;
    /** Иконка слева от текста. */
    icon?: ReactNode;
    disabled?: boolean;
}

export interface ISelectableListProps {
    items: ISelectableListItem[];
    /** Выбранные ключи; состояние ведёт родитель. */
    selected: readonly string[];
    onChange: (keys: string[]) => void;
    /** `single` — выбор одной строки (радио), `multiple` — набора (чекбоксы),
     *  `none` — список без выбора (например, выбранные файлы перед отправкой). */
    mode?: 'single' | 'multiple' | 'none';
    /** Строка «Выбрать все» над списком (только для multiple). */
    allowSelectAll?: boolean;
    /** Что показать вместо списка, когда выбирать нечего. */
    emptyText: string;
    /** Подпись списка для скринридера. */
    ariaLabel: string;
    /** Кнопка удаления строки. Удаляет владелец: строка уходит, когда он решит. */
    onRemove?: (key: string) => void;
    /** Свои действия в строке (проверить, скачать) — справа, перед удалением. */
    renderActions?: (item: ISelectableListItem) => ReactNode;
    /** Подсказка кнопки удаления. */
    removeTitle?: string;
    className?: string;
}

/**
 * Список с выбором строк — замена гриду там, где от него нужны только строки и
 * галочки: выбор вложений, файлов, этапов маршрута.
 *
 * Презентационный: выбор держит родитель и он же решает, что с ним делать.
 * Строка-карточка в языке блока аттачей (как список напоминаний): иконка,
 * заголовок с пояснением, значение справа.
 */
const SelectableList = ({
    items,
    selected,
    onChange,
    mode = 'multiple',
    allowSelectAll = false,
    emptyText,
    ariaLabel,
    onRemove,
    renderActions,
    removeTitle = 'Удалить',
    className,
}: ISelectableListProps) => {
    if (items.length === 0) {
        return <div className="selectable-list__empty">{emptyText}</div>;
    }

    const selectedSet = new Set(selected);
    const selectable = items.filter((item) => !item.disabled);
    const allSelected = selectable.length > 0 && selectable.every((item) => selectedSet.has(item.key));

    const toggle = (item: ISelectableListItem) => {
        if (item.disabled) return;
        if (mode === 'single') {
            onChange(selectedSet.has(item.key) ? [] : [item.key]);
            return;
        }
        const next = new Set(selectedSet);
        if (next.has(item.key)) next.delete(item.key);
        else next.add(item.key);
        onChange([...next]);
    };

    const toggleAll = () => onChange(allSelected ? [] : selectable.map((item) => item.key));

    return (
        <div className={clsx('selectable-list', className)}>
            {mode === 'multiple' && allowSelectAll && (
                <label className="selectable-list__all">
                    <input
                        type="checkbox"
                        checked={allSelected}
                        ref={(el) => {
                            // частичный выбор виден на самой галочке, а не отдельной подписью
                            if (el) el.indeterminate = !allSelected && selectable.some((i) => selectedSet.has(i.key));
                        }}
                        onChange={toggleAll}
                    />
                    Выбрать все
                </label>
            )}
            <div className="selectable-list__rows" role="group" aria-label={ariaLabel}>
                {items.map((item) => {
                    const checked = selectedSet.has(item.key);
                    const Row = mode === 'none' ? 'div' : 'label';
                    return (
                        <Row
                            key={item.key}
                            className={clsx(
                                'selectable-list__row',
                                checked && 'is-selected',
                                item.disabled && 'is-disabled',
                                mode === 'none' && 'is-plain',
                            )}
                        >
                            {mode !== 'none' && (
                                <input
                                    type={mode === 'single' ? 'radio' : 'checkbox'}
                                    name={mode === 'single' ? ariaLabel : undefined}
                                    checked={checked}
                                    disabled={item.disabled}
                                    onChange={() => toggle(item)}
                                />
                            )}
                            {item.icon && (
                                <span className="selectable-list__icon" aria-hidden>
                                    {item.icon}
                                </span>
                            )}
                            <span className="selectable-list__main">
                                <span className="selectable-list__title" title={item.title}>
                                    {item.title}
                                </span>
                                {item.subtitle && (
                                    <span className="selectable-list__subtitle" title={item.subtitle}>
                                        {item.subtitle}
                                    </span>
                                )}
                            </span>
                            {item.meta != null && <span className="selectable-list__meta">{item.meta}</span>}
                            {renderActions && <span className="selectable-list__actions">{renderActions(item)}</span>}
                            {onRemove && (
                                <button
                                    type="button"
                                    className="selectable-list__remove"
                                    title={removeTitle}
                                    aria-label={`${removeTitle}: ${item.title}`}
                                    onClick={() => onRemove(item.key)}
                                >
                                    <MdDeleteOutline size={16} />
                                </button>
                            )}
                        </Row>
                    );
                })}
            </div>
        </div>
    );
};

export default SelectableList;
